import { forwardRef } from 'react';
import { Paper as MuiPaper, PaperProps as MuiPaperProps } from '@mui/material';

/**
 * Paper component - An elevated surface for grouping content.
 *
 * Paper is a fundamental component for creating visual hierarchy through elevation (shadows).
 * It's used for panels, cards, and containers that need to stand out from the background.
 *
 * ## When to Use Paper
 *
 * **Use Paper when:**
 * - Creating panels or cards that need elevation
 * - Building modals or dialogs (use elevation={8}+)
 * - Grouping related content with visual separation
 * - Creating action panels or floating UI elements
 *
 * **Don't use Paper when:**
 * - Simple grouping without elevation (use Box instead)
 * - Building complex cards (use Card component)
 * - Need just borders without shadows (use outlined variant or Box)
 *
 * ## Elevation Guidelines
 *
 * MUI's elevation system represents depth:
 * - **0**: Flat, no shadow (same as background)
 * - **1**: Subtle elevation for cards (default)
 * - **2**: Slightly raised elements
 * - **3**: Panels and action surfaces (common in mobile)
 * - **4-8**: Modals and popovers
 * - **16-24**: Top-layer elements (rarely used)
 *
 * ## Mobile App Usage
 *
 * Based on 5 uses in mobile app:
 * - Fixed position panels with elevation={3}
 * - Elevated containers for map actions
 * - Form containers
 *
 * @example
 * // Basic elevated card (default elevation={1})
 * <Paper>
 *   <Typography>Content</Typography>
 * </Paper>
 *
 * @example
 * // Panel with higher elevation
 * <Paper elevation={3} sx={{ p: 2 }}>
 *   <Typography variant="h6">Action Panel</Typography>
 *   <Button>Action</Button>
 * </Paper>
 *
 * @example
 * // Outlined variant (border instead of shadow)
 * <Paper variant="outlined" sx={{ p: 2 }}>
 *   <Typography>Content with border</Typography>
 * </Paper>
 *
 * @example
 * // Fixed position panel (mobile pattern)
 * <Paper
 *   elevation={3}
 *   sx={{
 *     position: 'fixed',
 *     bottom: 0,
 *     left: 0,
 *     right: 0,
 *     p: 2
 *   }}
 * >
 *   <Typography>Fixed Panel</Typography>
 * </Paper>
 */
export type PaperProps = MuiPaperProps;

/**
 * Paper component wrapper around MUI Paper.
 */
export const Paper = forwardRef<HTMLDivElement, PaperProps>((props, ref) => (
  <MuiPaper ref={ref} {...props} />
));

Paper.displayName = 'Paper';
